<template>
    <!-- Title, the "Submission link" field, the helper line and both footer buttons are verbatim
         from the UI handoff ("On-Demand Requests - UI Handoff", MODAL 2). -->
    <TprmModal
        open
        additional-class-name="RequestCreatedModal"
        title="New submission link created"
        :subtitle="subtitle"
        @close="close"
    >
        <p class="RequestCreatedModal__body" data-test-id="RequestCreatedModal__body">
            {{ bodyText }}
        </p>

        <div
            v-if="submissionLink"
            class="RequestCreatedModal__field"
            data-test-id="RequestCreatedModal__linkSection"
        >
            <div class="RequestCreatedModal__fieldLabel">
                Submission link
            </div>
            <div class="RequestCreatedModal__linkRow">
                <!-- The handoff asks for the URL in full, wrapping rather than truncated, but it
                     was drawn against a short mock link; a real submission URL wraps to several
                     lines and dominates the modal. Truncated to one line with the full value on
                     the element's title, and Copy remains the way to take it whole. -->
                <div
                    class="RequestCreatedModal__link"
                    data-test-id="RequestCreatedModal__link"
                    :title="submissionLink"
                >
                    {{ submissionLink }}
                </div>
                <NewButton
                    variant="outline"
                    color="primary"
                    class="RequestCreatedModal__copyButton"
                    data-test-id="RequestCreatedModal__copy"
                    @click="copyLink"
                >
                    Copy
                </NewButton>
            </div>
        </div>

        <p class="RequestCreatedModal__helper" data-test-id="RequestCreatedModal__footer">
            You can retrieve this link any time from the entity menu under
            <strong>Copy submission link to clipboard</strong>.
        </p>

        <template #footer>
            <NewButton
                variant="outline"
                color="neutral"
                data-test-id="RequestCreatedModal__close"
                @click="close"
            >
                Close
            </NewButton>
            <NewButton
                v-if="submissionLink"
                color="primary"
                data-test-id="RequestCreatedModal__open"
                @click="openLink"
            >
                Open link in new tab
            </NewButton>
        </template>
    </TprmModal>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import {
        ComplianceStatus,
        VerificationStatus,
        VerificationStatusLabel,
    } from '@evidentid/tprm-portal-lib/models/entity-details';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';

    const COMPLIANT_BODY_LEAD = 'A new submission link has been created for the requirements you selected.';

    // TODO PRODUCT-26539: design supplied three variants -- Compliant, Exhausted, No Response -- and
    // a non-compliant entity whose last verification finished is none of them, yet it is an ordinary
    // state: documents were submitted and failed criteria. It gets the exhausted sentence because
    // that sentence is true of it -- a link exists and the entity can retry what is outstanding,
    // which is the scope the server resolves for any non-compliant entity -- and rendering nothing
    // would leave the modal's only paragraph blank for a common case.
    const RETRY_BODY = 'A new submission link has been created. The entity can use it to retry the '
        + 'requirements that were not completed.';

    // Declares every event this component `$emit`s -- without a declared `emits`, Vue 3 warns
    // "Component emitted event 'close' but it is neither declared in the emits option nor as an
    // 'onClose' prop" every time it fires.
    @Component({
        components: {
            NewButton,
            TprmModal,
        },
        emits: [ 'close' ],
    })
    export default class RequestCreatedModal extends Vue {
        @Prop({ type: Object, required: true })
        private entity!: Entity;

        // The variant is chosen from the entity's state before the request was sent, because
        // afterwards every entity is Pending Submission.
        @Prop({ type: String, required: true })
        private priorVerificationStatus!: VerificationStatus;

        @Prop({ type: Number, default: 0 })
        private reopenedCount!: number;

        @Prop({ type: String, default: undefined })
        private submissionLink?: string;

        @Prop({ type: Boolean, default: false })
        private notificationSuppressed!: boolean;

        /** The count belongs to a compliant entity, which is the only one offered a requirement
         *  picker; for any other the count would report a scope the server chose, against a
         *  selection the user was never shown. What distinguishes those is the state they came
         *  from, and `complete` is deliberately unlabelled, which leaves the name standing alone. */
        private get subtitleDetail(): string {
            if (this.entity.complianceStatus === ComplianceStatus.compliant) {
                const noun = this.reopenedCount === 1 ? 'requirement' : 'requirements';
                return `${this.reopenedCount} ${noun} reopened`;
            }
            switch (this.priorVerificationStatus) {
                case VerificationStatus.exhausted:
                    return VerificationStatusLabel.exhausted;
                case VerificationStatus.noInsuredResponse:
                    return VerificationStatusLabel.noInsuredResponse;
                default:
                    return '';
            }
        }

        private get subtitle(): string {
            return this.subtitleDetail
                ? `${this.entity.displayName} \u00b7 ${this.subtitleDetail}`
                : this.entity.displayName;
        }

        /** Whether no email went out. `notificationSuppressed` only echoes the email toggle, so a
         *  paused entity has to be read off the entity itself.
         *
         *  Leaving a paused entity unnotified is the product rule, not an oversight: it gets no
         *  request mail, no reminder cadence, and a manual resend is refused as well. The link on
         *  this modal is the only way one ever reaches the entity, which is why the copy must not
         *  claim an email was sent. */
        private get emailSuppressed(): boolean {
            return this.notificationSuppressed || this.entity.paused;
        }

        /** Keyed on compliance, not on the verification status, because "the requirements you
         *  selected" is only ever true of an entity Modal 1 showed a picker to, and that is the
         *  compliant ones. */
        private get bodyText(): string {
            if (this.entity.complianceStatus === ComplianceStatus.compliant) {
                // TODO PRODUCT-26539: design has not supplied copy for the case where no email is
                // sent. The normal second sentence asserts the entity was emailed, which is false
                // here, so it is left off rather than replaced with an invented substitute.
                return this.emailSuppressed
                    ? COMPLIANT_BODY_LEAD
                    : `${COMPLIANT_BODY_LEAD} The entity has been emailed and will receive the normal reminder cadence.`;
            }
            switch (this.priorVerificationStatus) {
                case VerificationStatus.complete:
                case VerificationStatus.exhausted:
                    return RETRY_BODY;
                case VerificationStatus.noInsuredResponse:
                    return 'A new submission link has been created. The entity can use it to start the '
                        + 'submission they have not yet responded to.';
                default:
                    return '';
            }
        }

        /** Awaited, because the write genuinely fails -- denied permission, or any insecure
         *  context -- and claiming success on a clipboard that still holds something else leaves
         *  the user pasting the wrong thing with no sign anything went wrong. The link stays on
         *  screen either way, so a failure is recoverable by selecting it.
         */
        private async copyLink(): Promise<void> {
            if (!this.submissionLink) {
                return;
            }
            try {
                await navigator.clipboard.writeText(this.submissionLink);
            } catch {
                useSnackbarStore().displaySnackbar({
                    success: false,
                    message: 'Could not copy the submission link. Select it to copy it manually.',
                });
                return;
            }
            useSnackbarStore().displaySnackbar({
                success: true,
                message: 'Submission link has been copied to clipboard.',
            });
        }

        /** `noopener` is set explicitly: the submission experience is a separate origin and must
         *  not get a handle back to the portal window. */
        private openLink(): void {
            if (!this.submissionLink) {
                return;
            }
            window.open(this.submissionLink, '_blank', 'noopener');
        }

        private close(): void {
            this.$emit('close');
        }
    }
</script>
